// Modal para editar un pago (monto, fecha, observación)
import React, {useEffect, useState} from 'react'
import {KTIcon} from '../../../../_metronic/helpers'
import {fetchWithAuth} from '../../../core/utils/fetchWithAuth'

export type PagoParaEditar = {
  idPago: string
  monto: string
  fecha: string
  observaciones?: string | null
}

type Props = {
  isOpen: boolean
  onClose: () => void
  pago: PagoParaEditar | null
  onSuccess: () => void
}

const EditarPagoModal: React.FC<Props> = ({isOpen, onClose, pago, onSuccess}) => {
  const [monto, setMonto] = useState('')
  const [fecha, setFecha] = useState('')
  const [observacion, setObservacion] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (pago) {
      setMonto(pago.monto || '')
      setFecha(pago.fecha ? pago.fecha.slice(0, 10) : '')
      setObservacion(pago.observaciones || '')
      setError(null)
    }
  }, [pago])

  const getToken = (): string | null => localStorage.getItem('X-TOKEN')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pago?.idPago) return
    const idPagoNum = parseInt(pago.idPago, 10)
    if (isNaN(idPagoNum)) return
    const montoVal = monto.trim()
    if (!montoVal) {
      setError('El monto es obligatorio.')
      return
    }
    const fechaVal = fecha.trim()
    if (!fechaVal) {
      setError('La fecha es obligatoria.')
      return
    }
    try {
      setSaving(true)
      setError(null)
      const token = getToken()
      if (!token) throw new Error('No se encontró el token de autenticación')

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token,
        },
        body: JSON.stringify({
          service: 'updatePago',
          pago: {
            idPago: idPagoNum,
            monto: montoVal,
            fecha: fechaVal,
            observacion: observacion.trim() || undefined,
          },
        }),
      })

      const data = await response.json()
      if (data.code === 'OK') {
        onSuccess()
        onClose()
      } else {
        throw new Error(data.returned_message || 'Error al actualizar el pago')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className='modal fade show d-block'
      tabIndex={-1}
      role='dialog'
      aria-modal='true'
      style={{zIndex: 2000, position: 'fixed', inset: 0}}
    >
      <div
        className='modal-dialog modal-dialog-centered'
        role='document'
        style={{position: 'relative', zIndex: 2001}}
      >
        <div className='modal-content'>
          <div className='modal-header bg-primary'>
            <h3 className='modal-title text-white d-flex align-items-center'>
              <KTIcon iconName='pencil' className='fs-2 me-2' />
              Editar pago
            </h3>
            <button
              type='button'
              className='btn btn-sm btn-icon btn-active-color-primary'
              aria-label='Cerrar'
              onClick={onClose}
            >
              <KTIcon iconName='cross' className='fs-2 text-white' />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className='modal-body'>
              {error && (
                <div className='alert alert-danger d-flex align-items-center mb-5'>
                  <KTIcon iconName='information-5' className='fs-2hx text-danger me-3' />
                  <span>{error}</span>
                </div>
              )}
              <div className='mb-5'>
                <label className='form-label fw-bold required'>Monto</label>
                <input
                  type='text'
                  className='form-control form-control-solid'
                  placeholder='Ej: 650.50'
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  required
                />
              </div>
              <div className='mb-5'>
                <label className='form-label fw-bold required'>Fecha</label>
                <input
                  type='date'
                  className='form-control form-control-solid'
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  required
                />
              </div>
              <div className='mb-0'>
                <label className='form-label fw-bold'>Observación</label>
                <textarea
                  className='form-control form-control-solid'
                  rows={3}
                  placeholder='Observaciones (opcional)'
                  value={observacion}
                  onChange={(e) => setObservacion(e.target.value)}
                />
              </div>
            </div>
            <div className='modal-footer border-0 pt-0'>
              <button type='button' className='btn btn-light' onClick={onClose}>
                Cancelar
              </button>
              <button type='submit' className='btn btn-primary' disabled={saving}>
                {saving ? (
                  <>
                    <span className='spinner-border spinner-border-sm me-2' />
                    Guardando...
                  </>
                ) : (
                  'Guardar'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className='modal-backdrop fade show' style={{zIndex: 1990}} />
    </div>
  )
}

export {EditarPagoModal}
